import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { getImage } from "gatsby-plugin-image";

// Drop-in v2-compatible image renderer that uses gatsby-plugin-image v3's
// processed data (gatsbyImageData) but emits the SAME DOM structure as
// gatsby-image v2 — so the entire codebase's existing CSS targeting
// `.gatsby-image-wrapper` keeps working without modification.
//
// v2 structure (which our SCSS expects):
//   <div class="gatsby-image-wrapper">
//     <div style="padding-bottom: X%"></div>   <!-- aspect ratio spacer -->
//     <picture>
//       <source type="image/webp" srcSet="..." />
//       <img src="..." srcSet="..." style="position:absolute;width:100%;height:100%;object-fit:cover" />
//     </picture>
//   </div>
//
// v3's <GatsbyImage> emits an extra inner div with max-width / inline aspect
// ratio that conflicts with our SCSS — every component that styles
// .gatsby-image-wrapper had to be patched. This component avoids that entirely.

/**
 * `priority` — obrazek jest nad zgięciem i ma się ładować od razu.
 *
 * Domyślnie WSZYSTKIE obrazki idą leniwie. Wcześniej było odwrotnie i nic nie
 * było leniwe: strona główna ściągała na telefonie 33 pliki i 1,05 MB, zanim
 * ktokolwiek dotknął ekranu, przy stronie wysokiej na 36 ekranów. Zmierzone
 * na produkcji, obie szablony tak samo.
 *
 * 🔴 W kodzie stał komentarz, że `loading="lazy"` zostało zdjęte celowo, bo
 * przy nawigacji po stronie obrazki potrafiły zostać puste do odświeżenia.
 * Sprawdziłem to: 18 przejść klientem w Chromium i WebKicie, na trzech
 * szablonach — każdy widoczny obrazek wczytany. Wyścig nie występuje na
 * obecnym Gatsbym i Reakcie, więc lazy wraca jako domyślne.
 *
 * Jeśli kiedyś wróci: objawia się pustym kadrem po kliknięciu w link
 * wewnętrzny, znikającym po odświeżeniu — wtedy trzeba go zdiagnozować,
 * a nie wyłączać lazy na całym serwisie.
 */
const PreviewCompatibleImage = ({ imageInfo, styles = {}, className = "", priority = false }) => {
  const { alt = "", title = "", childImageSharp, image } = imageInfo;

  // 🔴 Hooki przed jakimkolwiek `return` — ścieżka SVG niżej kończy render
  // wcześniej, a kolejność hooków musi być identyczna przy każdym wywołaniu.
  const imgRef = useRef<HTMLImageElement>(null);
  const [wczytany, setWczytany] = useState(false);

  useEffect(() => {
    // Obrazek mógł skończyć się ładować, ZANIM React podpiął `onLoad`. Dla
    // obrazów nad zgięciem to reguła, nie wyjątek: przeglądarka pobiera je
    // prosto z HTML-a, hydracja przychodzi później. Bez tego sprawdzenia
    // rozmycie zostawałoby na wierzchu na zawsze.
    //
    // 🔴 `naturalWidth`, nie samo `complete`. Obraz, który się NIE wczytał,
    // też raportuje `complete === true` — samo `complete` gasiło więc rozmycie
    // przy zerwanym pobraniu i zostawiało pusty kadr dokładnie wtedy, gdy
    // podkład jest najbardziej potrzebny.
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) setWczytany(true);
  }, []);

  // Path 1: image is a string (e.g., imported SVG)
  if (image && typeof image === "string") {
    return (
      <img
        style={{ borderRadius: "5px", ...styles }}
        className={className}
        src={image}
        alt={alt}
        title={title}
      />
    );
  }

  // Path 2/3: extract gatsbyImageData
  const data = getImage(image) || (childImageSharp ? getImage({ childImageSharp } as any) : null);
  if (!data) return null;

  const fallback = (data as any).images?.fallback || {};
  const sources = (data as any).images?.sources || [];
  // Base64 LQIP from gatsby-plugin-sharp `placeholder: BLURRED`.
  // Inline JPEG/PNG, ~0,5-1,5 KB, dekoduje się w pierwszym malowaniu.
  //
  // Rozmycie dostają WSZYSTKIE obrazy. Wcześniej warstwa szła wyłącznie pod
  // źródła JPEG, bo przy wyciętym tle (zdjęcia lekarzy, ikony, ilustracje)
  // rozmyta sylwetka wychodzi poza ostrą i zostaje jako brudna poświata wokół
  // włosów — przezroczysty obrazek nigdy jej nie zakryje. Skutek uboczny: 439
  // z 491 rozmyć w serwisie było wyrzucanych, więc 89% zdjęć wskakiwało w pusty
  // kadr, a rozmycie widać było tylko pod nielicznymi JPEG-ami.
  //
  // Poświata brała się z tego, że warstwa zostawała pod spodem NA STAŁE.
  // Teraz gaśnie, gdy zdjęcie się wczyta — patrz `wczytany`.
  const placeholder = (data as any).placeholder?.fallback as string | undefined;
  const bgColor = (data as any).backgroundColor as string | undefined;
  const aspectPadding = data.height && data.width
    ? `${(data.height / data.width) * 100}%`
    : "100%";

  return (
    <div
      className={`gatsby-image-wrapper ${className}`}
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "5px",
        backgroundColor: bgColor,
        ...styles,
      }}
    >
      {/* aspect-ratio spacer (mirrors gatsby-image v2 padding-bottom hack) */}
      <div aria-hidden="true" style={{ width: "100%", paddingBottom: aspectPadding }} />
      {/* Warstwa rozmycia widoczna, dopóki leci właściwy plik. `background-image`,
          nie `<img>` — jedzie w SSR, nie generuje drugiego żądania i nie trafia
          do drzewa dostępności.

          Wygaszenie, nie odmontowanie: 200 ms przykrywa klatkę między zdarzeniem
          `load` a faktycznym namalowaniem (`decoding="async"`), więc pod ostrym
          zdjęciem nigdy nie mignie tło kontenera. */}
      {placeholder && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url("${placeholder}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            opacity: wczytany ? 0 : 1,
            transition: "opacity 200ms ease",
            pointerEvents: "none",
          }}
        />
      )}
      <picture>
        {sources.map((s: any, i: number) => (
          <source key={i} srcSet={s.srcSet} sizes={s.sizes} type={s.type} />
        ))}
        <img
          ref={imgRef}
          onLoad={() => setWczytany(true)}
          src={fallback.src}
          srcSet={fallback.srcSet}
          sizes={fallback.sizes}
          alt={alt}
          title={title}
          decoding="async"
          // Leniwie wszystko poza tym, co jawnie oznaczone jako nadzgięciowe.
          // Warstwa rozmytego LQIP wyżej sprawia, że kadr nigdy nie jest pusty
          // — pacjent widzi rozmyty obraz, a nie dziurę.
          loading={priority ? "eager" : "lazy"}
          {...(priority ? { fetchpriority: "high" } : {})}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />
      </picture>
    </div>
  );
};

PreviewCompatibleImage.propTypes = {
  imageInfo: PropTypes.shape({
    alt: PropTypes.string,
    title: PropTypes.string,
    childImageSharp: PropTypes.object,
    image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    style: PropTypes.object,
  }).isRequired,
};

export default PreviewCompatibleImage;
